import {useContext, useEffect, useState} from "react"
import {Button, Form} from "react-bootstrap"
import {Calendar} from "primereact/calendar"
import {Dropdown} from "primereact/dropdown"
import {Spinner} from "react-bootstrap"

import {DirectoryStatusAlert} from "../../components/admin/DirectoryStatusAlert.jsx"
import SubHeader from "../../components/SubHeader.jsx"
import {wrappedGet, wrappedPost} from "../../utilities/AxiosHelper.jsx"
import {getJSDate} from "../../utilities/general.jsx"
import RefContext from "../../utilities/RefContext.jsx"
import {getToken} from "../../utilities/useToken.jsx"
import {useStableNavigate} from "../../utilities/StableNavigateContext"

const initialUser = {
    account: "",
    email: "",
    employeeFTE: "",
    employeeId: "",
    facilityId: null,
    firstName: "",
    hireDate: "",
    lastName: "",
    nursingProgram: null,
    organizationId: null,
    orientationLength: "20",
    personalEmail: "",
    positionId: null,
    priorExperience: null,
    surveyId: null,
    unitId: null,
}

const NewOrientation = () => {
    const navigate = useStableNavigate()
    useEffect(() => {
        document.title = "New Orientation"
    }, [])
    const {dispatch} = useContext(RefContext)
    const [user, setUser] = useState(initialUser)
    const [isLoading, setIsLoading] = useState(false)
    const [organizationId, setOrganizationId] = useState(getToken()?.organization_id || null)
    useEffect(() => {
        if (!organizationId) {
            fetchOrganizations()
        } else {
            fetchFacilities()
            fetchPositions()
        }
    }, [organizationId])

    const [organizations, setOrganizations] = useState([])
    const [facilities, setFacilities] = useState([])
    const [units, setUnits] = useState([])
    const [positions, setPositions] = useState([])
    const [isAccountInDirectory, setIsAccountInDirectory] = useState(undefined)

    const handleAddOrientation = async () => {
        // validate required fields
        try {
            const payload = {
                account: user.account,
                email: user.email,
                employee_fte: user.employeeFTE,
                employee_id: user.employeeId,
                first_name: user.firstName,
                hire_date: user.hireDate,
                last_name: user.lastName,
                organization_id: organizationId,
                facility_id: user.facilityId,
                unit_id: user.unitId,
                orientation_length: user.orientationLength,
                position_id: user.positionId,
            }

            const addOrientation = await wrappedPost(`/admin/orientation-add`, payload)
            dispatch("showToast", {
                show: true,
                variant: "success",
                text: `Orientation added for ${user.firstName} ${user.lastName}`,
                timeout: 7000,
            })
            setUser(initialUser)
            setIsAccountInDirectory(undefined)
            setUnits([])
            navigate(`/educator-onboarding/${addOrientation.data.orientation_id}`)
        } catch (err) {
            dispatch("showToast", {
                show: true,
                variant: "danger",
                text: err.response.data["Error"] || "Unknown Error",
                timeout: 7000,
            })
        }
    }

    const adLookup = () => {
        setIsLoading(true)
        wrappedGet(`ad-lookup/${user.account}`).then((response) => {
            const user = response.data.user
            if (user) {
                updateUser("firstName", user.first_name)
                updateUser("lastName", user.last_name)
                updateUser("email", user.email)
                updateUser("employeeId", user.employee_id)
                setIsAccountInDirectory(true)
            } else {
                setIsAccountInDirectory(false)
            }
            setIsLoading(false)
        })
    }

    const fetchOrganizations = () => {
        return wrappedGet("get-organizations")
            .then((response) => {
                if (response.data && Array.isArray(response.data.results) && response.data.results.length > 0) {
                    setOrganizations(response.data.results)
                }
            })
            .catch(() => {
                dispatch("showToast", {
                    show: true,
                    variant: "danger",
                    text: "Problem fetching Organizations",
                    timeout: 5000,
                })
            })
    }

    const fetchPositions = () => {
        return wrappedGet(`/organization/${organizationId}/position-list`)
            .then((response) => {
                if (response.data && Array.isArray(response.data.results) && response.data.results.length > 0) {
                    setPositions(response.data.results)
                }
            })
            .catch(() => {
                dispatch("showToast", {
                    show: true,
                    variant: "danger",
                    text: "Problem fetching Positions",
                    timeout: 5000,
                })
            })
    }

    const fetchFacilities = () => {
        return wrappedGet(`organization/${organizationId}/facility`)
            .then((response) => {
                if (response.data && Array.isArray(response.data.results) && response.data.results.length > 0) {
                    setFacilities(response.data.results)
                }
            })
            .catch(() => {
                dispatch("showToast", {
                    show: true,
                    variant: "danger",
                    text: "Problem fetching Facilities",
                    timeout: 5000,
                })
            })
    }

    const fetchUnits = (facilityId) => {
        setUnits([])
        if (facilityId) {
            return wrappedGet(`organization/${organizationId}/facility/${facilityId}/unit`)
                .then((response) => {
                    if (response.data && Array.isArray(response.data.results) && response.data.results.length > 0) {
                        setUnits(response.data.results)
                    }
                })
                .catch(() => {
                    dispatch("showToast", {
                        show: true,
                        variant: "danger",
                        text: "Problem fetching Units",
                        timeout: 5000,
                    })
                })
        }
    }

    function updateUser(field, value) {
        setUser((prev) => ({...prev, [field]: value}))
    }

    return (
        <>
            <SubHeader title2="New Hire Onboarding & Orientation Information" icon="fa-users" />
            <div className="app-container container d-flex">
                <div className="app-main flex-column flex-row-fluid py-10">
                    {!organizationId ? (
                        <div className="row d-flex align-items-end mb-4">
                            <div className="col-lg-6 mb-4">
                                <div className="form-label required">Organization</div>
                                <Dropdown
                                    className="w-100 p-dropdown-tall"
                                    panelClassName="show-10"
                                    showClear={true}
                                    name="organization"
                                    value={organizationId}
                                    options={organizations?.map((org) => {
                                        return {label: org.name, value: org.id}
                                    })}
                                    filter={false}
                                    onChange={(evt) => {
                                        setOrganizationId(evt.target.value)
                                    }}
                                />
                            </div>
                        </div>
                    ) : (
                        <Form
                            onSubmit={(e) => {
                                e.preventDefault()
                                adLookup()
                            }}
                        >
                            <Form.Group className="mb-3">
                                <Form.Label htmlFor="accountName" className="required">
                                    Active Directory Account Name
                                </Form.Label>
                                <div className="d-flex align-items-start">
                                    <Form.Control
                                        name="accountName"
                                        placeholder="Enter Active Directory Account Name"
                                        onChange={(evt) => updateUser("account", evt.target.value)}
                                        value={user.account}
                                        required
                                    />
                                    <Button type="button" className="ms-2 text-nowrap" onClick={adLookup}>
                                        <span aria-live="polite">Load from Directory</span>
                                    </Button>
                                </div>
                            </Form.Group>
                            {isLoading && (
                                <div className="d-flex justify-content-center align-items-center">
                                    <div className="d-flex align-items-center">
                                        <span className="me-2 fs-1">Please wait...</span>
                                        <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                                    </div>
                                </div>
                            )}
                            <DirectoryStatusAlert isAccountInDirectory={isAccountInDirectory} />
                            {!isLoading && isAccountInDirectory && (
                                <>
                                    <hr />
                                    <div className="row d-flex align-items-end mb-4">
                                        <div className="fv-row col-6 mb-7 fv-plugins-icon-container">
                                            <Form.Label htmlFor="firstName" className="required">
                                                First Name
                                            </Form.Label>
                                            <Form.Control
                                                required
                                                type="text"
                                                name="firstName"
                                                placeholder="First name"
                                                value={user.firstName}
                                                disabled
                                            />
                                        </div>
                                        <div className="fv-row col-6 mb-7 fv-plugins-icon-container">
                                            <Form.Label htmlFor="lastName" className="required">
                                                Last Name
                                            </Form.Label>
                                            <Form.Control
                                                required
                                                type="text"
                                                name="lastName"
                                                placeholder="Last name"
                                                value={user.lastName}
                                                disabled
                                            />
                                        </div>
                                    </div>
                                    <div className="row d-flex align-items-end mb-4">
                                        <div className="fv-row mb-7 fv-plugins-icon-container col-6">
                                            <Form.Label className="required">Email</Form.Label>
                                            <Form.Control
                                                type="email"
                                                name="email"
                                                placeholder="Email"
                                                value={user.email}
                                                disabled
                                            />
                                        </div>
                                        <div className="fv-row mb-7 fv-plugins-icon-container col-6">
                                            <Form.Label className="required">Employee ID</Form.Label>
                                            <Form.Control
                                                type="employee_id"
                                                name="employee_id"
                                                placeholder="Employee ID"
                                                value={user.employeeId}
                                                disabled
                                            />
                                        </div>
                                    </div>
                                    <div className="row d-flex align-items-end mb-4">
                                        <div className="col-lg-6 mb-4">
                                            <div className="form-label required">Facility</div>
                                            <Dropdown
                                                className="w-100 p-dropdown-tall"
                                                panelClassName="show-10"
                                                showClear={true}
                                                name="facility"
                                                value={user.facilityId}
                                                options={facilities.map((facility) => {
                                                    return {label: facility.name, value: facility.id}
                                                })}
                                                filter={false}
                                                onChange={(evt) => {
                                                    updateUser("facilityId", evt.target.value)
                                                    updateUser("unitId", null)
                                                    fetchUnits(evt.target.value)
                                                }}
                                            />
                                            {/* <div className="fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback">
                                                    {errorFacility}
                                                </div> */}
                                        </div>
                                        <div className="col-lg-6 mb-4">
                                            <div className="form-label required">Unit</div>
                                            <Dropdown
                                                className="w-100 p-dropdown-tall"
                                                panelClassName="show-10"
                                                showClear={true}
                                                name="unit"
                                                value={user.unitId}
                                                options={units.map((unit) => {
                                                    return {label: unit.name, value: unit.id}
                                                })}
                                                filter={false}
                                                onChange={(evt) => {
                                                    updateUser("unitId", evt.target.value)
                                                }}
                                            />
                                            {/* <div className="fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback">
                                                    {errorFacility}
                                                </div> */}
                                        </div>
                                    </div>
                                    <div className="row d-flex align-items-end mb-4">
                                        <div className="col-lg-6 mb-4">
                                            <Form.Label htmlFor="hireDate" className="required">
                                                Hire Date
                                            </Form.Label>
                                            <Calendar
                                                showIcon
                                                className="w-100 p-dropdown-tall"
                                                baseZIndex={1500}
                                                placeholder="Hire Date"
                                                value={getJSDate(user.hireDate)}
                                                onChange={(e) => updateUser("hireDate", e.value)}
                                            />
                                        </div>
                                        <div className="col-lg-6 mb-4">
                                            <Form.Label htmlFor="employeeFTE" className="required">
                                                Employee FTE
                                            </Form.Label>
                                            <Form.Control
                                                name="employeeFTE"
                                                type="number"
                                                required
                                                value={user.employeeFTE}
                                                onChange={(evt) => updateUser("employeeFTE", evt.target.value)}
                                            />
                                        </div>
                                    </div>
                                    <div className="row d-flex align-items-end mb-4">
                                        <div className="col-lg-6 mb-4">
                                            <div className="form-label required">Position</div>
                                            <Dropdown
                                                className="w-100 p-dropdown-tall"
                                                panelClassName="show-10"
                                                showClear={true}
                                                name="position"
                                                value={user.positionId}
                                                options={positions.map((pos) => {
                                                    return {label: pos.name, value: pos.id}
                                                })}
                                                filter={false}
                                                onChange={(evt) => updateUser("positionId", evt.target.value)}
                                            />
                                        </div>
                                    </div>
                                    <hr />
                                    <Button type="button" className="ms-2 text-nowrap" onClick={handleAddOrientation}>
                                        <span aria-live="polite">Submit</span>
                                    </Button>
                                </>
                            )}
                        </Form>
                    )}
                </div>
            </div>
        </>
    )
}

export default NewOrientation
